import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Paper, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Select, MenuItem, FormControl, InputLabel, IconButton, Typography,
  Stack, Card, CardContent, Box, Divider, Collapse, Chip,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  CircularProgress, Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';

const API_BASE = 'https://portal.peaksoftcon.com:8081/api';

const FILE_TITLES = [
  'Proje onay yazısı',
  'İş planı',
  'Proje bitirme belgesi',
];

function StatusChip({ label }) {
  if (!label || label === '-') return <span>-</span>;
  const color = /tamamland/i.test(label) ? 'success'
    : /değerlend|düzenleni/i.test(label) ? 'warning'
    : /red|iptal/i.test(label)           ? 'error'
    : /onay/i.test(label)                ? 'success'
    : 'default';
  return <Chip size="small" label={label} color={color} variant="outlined" />;
}

function ProjectsTable({ rows }) {
  if (!rows) return <CircularProgress size={20} />;
  if (rows.length === 0) return (
    <Alert severity="info" variant="outlined">
      Proje verisi yok — bot her gece 02:00'de otomatik çalışır.
    </Alert>
  );
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'action.hover' }}>
            <TableCell>Proje Kodu</TableCell>
            <TableCell>STB Kodu</TableCell>
            <TableCell>Proje Adı</TableCell>
            <TableCell>Kategori</TableCell>
            <TableCell>Onay Durumu</TableCell>
            <TableCell>Süre</TableCell>
            <TableCell>T. Başlangıç</TableCell>
            <TableCell>Tah. Bitiş</TableCell>
            <TableCell>Ek Süre</TableCell>
            <TableCell>Bitiş</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((p) => {
            const r = p.rawData || {};
            return (
              <TableRow key={p.id} hover>
                <TableCell>{r.ProjeKodu || '-'}</TableCell>
                <TableCell>{r.BSTBProjeKodu || '-'}</TableCell>
                <TableCell
                  sx={{ maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  title={r.Adi}
                >
                  {r.Adi || '-'}
                </TableCell>
                <TableCell>{r.ProjeKategorisi || '-'}</TableCell>
                <TableCell><StatusChip label={r.OnayDurumu} /></TableCell>
                <TableCell>{r.ProjeSuresi ? `${r.ProjeSuresi} ay` : '-'}</TableCell>
                <TableCell>{r.TeknoparkBaslangicTarihi || '-'}</TableCell>
                <TableCell>{r.TahminiBitisSuresi || '-'}</TableCell>
                <TableCell>{r.TalepEdilenTahminiBitisSuresi || '-'}</TableCell>
                <TableCell>{r.BitisTarihi || '-'}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function CompletionsTable({ rows }) {
  if (!rows) return <CircularProgress size={20} />;
  if (rows.length === 0) return (
    <Alert severity="info" variant="outlined">
      Sonlandırma verisi yok — bot her gece 02:15'de otomatik çalışır.
    </Alert>
  );
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'action.hover' }}>
            <TableCell>Proje Kodu</TableCell>
            <TableCell>Proje Adı</TableCell>
            <TableCell>Bakanlık Onay Kodu</TableCell>
            <TableCell>Bitirme Talep Durumu</TableCell>
            <TableCell>Bitiş Tarihi</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((p) => {
            const r = p.rawData || {};
            return (
              <TableRow key={p.id} hover>
                <TableCell>{r.ProjeKodu || '-'}</TableCell>
                <TableCell
                  sx={{ maxWidth: 240, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  title={r.Adi || r.ProjeAdi}
                >
                  {r.Adi || r.ProjeAdi || '-'}
                </TableCell>
                <TableCell>{r.BakanlıkOnayKodu || r.BakanlikOnayKodu || '-'}</TableCell>
                <TableCell><StatusChip label={r.BitirmeTalepDurumu} /></TableCell>
                <TableCell>{r.ProjeBitisTarihi || r.BitisTarihi || '-'}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function AdminProjectTracking() {
  const [companies,   setCompanies]   = useState([]);
  const [projects,    setProjects]    = useState({});     // cid → rows | null(loading)
  const [completions, setCompletions] = useState({});
  const [files,       setFiles]       = useState({});
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const [uploadOpen,  setUploadOpen]  = useState(false);
  const [uploadCid,   setUploadCid]   = useState(null);
  const [uploadFile,  setUploadFile]  = useState(null);
  const [uploadTitle, setUploadTitle] = useState(FILE_TITLES[0]);
  const [uploading,   setUploading]   = useState(false);

  useEffect(() => {
    axios.get(`${API_BASE}/bot/companies`)
      .then((r) => setCompanies(r.data || []))
      .catch((e) => console.error('companies fetch error:', e));
  }, []);

  const fetchProjects = useCallback((cid) => {
    setProjects((p) => ({ ...p, [cid]: null }));
    axios.get(`${API_BASE}/bot/companies/${cid}/projects`)
      .then((r) => setProjects((p) => ({ ...p, [cid]: r.data || [] })))
      .catch(() => setProjects((p) => ({ ...p, [cid]: [] })));
  }, []);

  const fetchCompletions = useCallback((cid) => {
    setCompletions((p) => ({ ...p, [cid]: null }));
    axios.get(`${API_BASE}/bot/companies/${cid}/completions`)
      .then((r) => setCompletions((p) => ({ ...p, [cid]: r.data || [] })))
      .catch(() => setCompletions((p) => ({ ...p, [cid]: [] })));
  }, []);

  const fetchFiles = useCallback((cid) => {
    axios.get(`${API_BASE}/projectFiles/${cid}`)
      .then((r) => setFiles((f) => ({ ...f, [cid]: r.data || [] })))
      .catch((e) => console.error('files fetch error:', e));
  }, []);

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        if (!(id in projects))    fetchProjects(id);
        if (!(id in completions)) fetchCompletions(id);
        if (!files[id])           fetchFiles(id);
      }
      return next;
    });
  };

  const openUpload = (cid) => {
    setUploadCid(cid);
    setUploadFile(null);
    setUploadTitle(FILE_TITLES[0]);
    setUploadOpen(true);
  };

  const submitUpload = async () => {
    if (!uploadFile || !uploadCid) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file',   uploadFile);
    fd.append('baslik', uploadTitle);
    try {
      await axios.post(`${API_BASE}/projectFiles/${uploadCid}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadOpen(false);
      fetchFiles(uploadCid);
    } catch (e) {
      console.error('upload error:', e);
    } finally {
      setUploading(false);
    }
  };

  const deleteFile = (fileId, cid) => {
    if (!window.confirm('Bu dosyayı silmek istediğinize emin misiniz?')) return;
    axios.delete(`${API_BASE}/projectFiles/files/${fileId}`)
      .then(() => fetchFiles(cid))
      .catch((e) => console.error('delete file error:', e));
  };

  const downloadFile = (fileId, fileName) => {
    const a = document.createElement('a');
    a.href = `${API_BASE}/projectFiles/files/${fileId}/download`;
    a.download = fileName || '';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>Proje Takibi</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Proje bilgileri ve sonlandırma listesi her gece otomatik güncellenir (02:00 / 02:15).
      </Typography>

      <Stack spacing={1.5}>
        {companies.length === 0 && (
          <Paper sx={{ py: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">Bot'a kayıtlı firma bulunamadı.</Typography>
          </Paper>
        )}

        {companies.map((company) => {
          const expanded    = expandedIds.has(company.id);
          const compProj    = projects[company.id];
          const compCompl   = completions[company.id];
          const compFiles   = files[company.id] || [];

          const projCount  = Array.isArray(compProj)  ? compProj.length  : 0;
          const complCount = Array.isArray(compCompl) ? compCompl.length : 0;

          return (
            <Card
              key={company.id}
              variant="outlined"
              sx={{
                borderLeft: '4px solid',
                borderLeftColor: projCount > 0 ? 'primary.main' : 'divider',
              }}
            >
              {/* ── Başlık ── */}
              <CardContent
                sx={{ cursor: 'pointer', '&:last-child': { pb: 1.5 } }}
                onClick={() => toggleExpand(company.id)}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                    <AccountTreeIcon fontSize="small" color="action" />
                    <Typography fontWeight={600}>{company.name}</Typography>
                    {projCount > 0 && (
                      <Chip size="small" label={`${projCount} proje`} color="primary" variant="outlined" />
                    )}
                    {complCount > 0 && (
                      <Chip size="small" label={`${complCount} sonlandırma`} color="warning" variant="outlined" />
                    )}
                    {compFiles.length > 0 && (
                      <Chip size="small" label={`${compFiles.length} dosya`} color="secondary" variant="outlined" />
                    )}
                  </Stack>
                  <ExpandMoreIcon
                    sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform .2s', flexShrink: 0 }}
                  />
                </Stack>
              </CardContent>

              {/* ── Genişletilmiş içerik ── */}
              <Collapse in={expanded}>
                <Divider />
                <CardContent>

                  {/* ─ Proje Bilgileri ─ */}
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <AccountTreeIcon fontSize="small" color="primary" />
                    <Typography variant="subtitle2" fontWeight={600}>Proje Bilgileri</Typography>
                  </Stack>
                  <Box sx={{ mb: 3 }}>
                    <ProjectsTable rows={compProj} />
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  {/* ─ Proje Sonlandırma Listesi ─ */}
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <AssignmentLateIcon fontSize="small" color="warning" />
                    <Typography variant="subtitle2" fontWeight={600}>Proje Sonlandırma Listesi</Typography>
                  </Stack>
                  <Box sx={{ mb: 3 }}>
                    <CompletionsTable rows={compCompl} />
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  {/* ─ Dosyalar ─ */}
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600}>Belgeler</Typography>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<CloudUploadIcon />}
                      onClick={() => openUpload(company.id)}
                    >
                      Dosya Yükle
                    </Button>
                  </Stack>

                  {compFiles.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">Henüz belge yüklenmemiş.</Typography>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'action.hover' }}>
                            <TableCell>Belge Türü</TableCell>
                            <TableCell>Dosya Adı</TableCell>
                            <TableCell>Tarih</TableCell>
                            <TableCell align="right">İşlem</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {compFiles.map((f) => (
                            <TableRow key={f.id} hover>
                              <TableCell>
                                <Chip size="small" label={f.baslik} variant="outlined" />
                              </TableCell>
                              <TableCell>{f.file_name}</TableCell>
                              <TableCell>
                                {f.uploaded_at ? new Date(f.uploaded_at).toLocaleDateString('tr-TR') : '-'}
                              </TableCell>
                              <TableCell align="right">
                                <IconButton size="small" onClick={() => downloadFile(f.id, f.file_name)}>
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" color="error" onClick={() => deleteFile(f.id, company.id)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Collapse>
            </Card>
          );
        })}
      </Stack>

      {/* ── Dosya yükleme dialogu ── */}
      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Belge Yükle</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel>Belge Türü</InputLabel>
            <Select
              label="Belge Türü"
              value={uploadTitle}
              onChange={(e) => setUploadTitle(e.target.value)}
            >
              {FILE_TITLES.map((t) => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" component="label" fullWidth>
            {uploadFile ? uploadFile.name : 'Dosya Seç…'}
            <input type="file" hidden onChange={(e) => setUploadFile(e.target.files[0])} />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)} disabled={uploading}>İptal</Button>
          <Button
            variant="contained"
            onClick={submitUpload}
            disabled={!uploadFile || uploading}
            startIcon={uploading ? <CircularProgress size={14} /> : null}
          >
            {uploading ? 'Yükleniyor…' : 'Yükle'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminProjectTracking;
